import { useCallback, useMemo, useState } from 'react';

import type { PersonalisationSlot } from '../../context/personalisation-typedefs';
import { usePersonalisationContext } from '../../context/PersonalisationContext';
import {
  buildGroupedSlotModel,
  getInactiveGroupSlotIds,
  getInputLineValuesForGroup,
  getSlotForInputLine,
  inferActiveGroupName
} from '../../utilities/buildGroupedSlotModel';
import type {
  AlphanumericValue,
  GroupedModeSelection,
  OptionGroup
} from '../../utilities/buildGroupedSlotModel';

/** Extract display text from an alphanumeric slot value. */
function getAlphanumericText(value: unknown): string {
  if (typeof value === 'string') return value;
  if (value && typeof value === 'object' && 'text' in value) {
    const text = (value as AlphanumericValue).text;
    return typeof text === 'string' ? text : '';
  }
  return '';
}

/** Extract font from an alphanumeric slot value. */
function getAlphanumericFont(value: unknown): string | null {
  if (value && typeof value === 'object' && 'font' in value) {
    const font = (value as AlphanumericValue).font;
    return font ?? null;
  }
  return null;
}


/** A value that an image or text slot selection can hold at runtime. */
type SlotSelectionValue = string | File | AlphanumericValue | null;

/** One rendered input line within the active option group. */
export interface InputLine {
  inputLineIndex: number;
  slot: PersonalisationSlot;
  inputValue: SlotSelectionValue;
}

/** A choice item for the Choose option selector. */
export interface OptionChoice {
  id: string;
  label: string;
}

/** Image data emitted by PersonalisationImageUploadSlot after a save. */
export interface ImageSavedData {
  selected: File;
  extraImages: Array<{ data: File; type: string }>;
  supportingImage: File | null;
}

export interface GroupedSlotViewModelResult {
  optionGroups: OptionGroup[];
  inputLineCount: number;
  inputLines: InputLine[];
  activeGroupName: string;
  optionSelectionChoices: OptionChoice[];
  shouldShowChooseOption: boolean;
  isImageUploadStep: boolean;
  hasUserProvidedContent: boolean;
  parsedAllowedFontsForGroup: string[];
  hasFontOptionsForGroup: boolean;
  selectedFontForGroup: string;
  fontPreviewLines: string[];
  handleGroupFontChange: (fontName: string) => void;
  handleInputLineChange: (inputLineIndex: number, slotId: string, newValue: unknown) => void;
  handleGroupChange: (newGroupName: string) => void;
  handleImageUploadedInGroup: (inputLineIndex: number, imageData: ImageSavedData) => void;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * View model for GROUPED slot mode.
 *
 * Terminology:
 * - **Option group** — a distinct `groupName` (e.g. Foil, Black, Gold); customer picks one via Choose option.
 * - **Input line** — one customer-facing field within the active option (e.g. UV line 1 / 2 / 3).
 *   Multiple backend slots share a groupName and map to input lines by `orderIndex`.
 */
export function useGroupedSlotViewModel({ slots }: { slots: PersonalisationSlot[] }): GroupedSlotViewModelResult {
  const { selections, updateSelections } = usePersonalisationContext();

  const { optionGroups, inputLineCount } = useMemo(() => buildGroupedSlotModel(slots), [slots]);

  // Cast selections.slots to the broader GroupedModeSelection type since the context's
  // SelectionSlot.selected is typed as `string | null` but holds richer values at runtime.
  const selectionSlots = selections.slots as unknown as GroupedModeSelection[];

  const inferredActiveGroupName = useMemo(
    () => inferActiveGroupName({ optionGroups, selections: selectionSlots }),
    [optionGroups, selectionSlots]
  );

  const [activeGroupNameOverride, setActiveGroupNameOverride] = useState<string | null>(null);

  const resolvedActiveGroupName = activeGroupNameOverride ?? inferredActiveGroupName;

  const activeOptionGroup = useMemo(
    () => optionGroups.find((group) => group.groupName === resolvedActiveGroupName) ?? optionGroups[0],
    [optionGroups, resolvedActiveGroupName]
  );

  const optionSelectionChoices = useMemo<OptionChoice[]>(
    () => optionGroups.map((group) => ({ id: group.groupName, label: group.groupName })),
    [optionGroups]
  );

  /** One entry per visible input line for the active option group. */
  const inputLines = useMemo<InputLine[]>(() => {
    if (!activeOptionGroup) return [];

    const result: InputLine[] = [];
    for (let inputLineIndex = 0; inputLineIndex < inputLineCount; inputLineIndex++) {
      const slot = getSlotForInputLine(activeOptionGroup, inputLineIndex);
      if (!slot) continue;

      const selection = selectionSlots.find((item) => item.id === slot.id);
      result.push({
        inputLineIndex,
        slot,
        inputValue: (selection?.selected ?? '') as SlotSelectionValue
      });
    }
    return result;
  }, [activeOptionGroup, inputLineCount, selectionSlots]);

  const inputLineHasContent = useCallback((value: unknown): boolean => {
    if (value instanceof File) return true;
    if (typeof value === 'string') return value.trim().length > 0;
    if (value && typeof value === 'object' && 'text' in value) {
      const text = (value as AlphanumericValue).text;
      return Boolean(text && String(text).trim().length > 0);
    }
    return Boolean(value);
  }, []);

  /** True once the customer has typed in any text line or uploaded an image (mirrors font gating). */
  const hasUserProvidedContent = useMemo(
    () => inputLines.some((line) => inputLineHasContent(line.inputValue)),
    [inputLines, inputLineHasContent]
  );

  const shouldShowChooseOption = optionSelectionChoices.length > 1 && hasUserProvidedContent;

  const isImageUploadStep =
    inputLines.length > 0 && inputLines.every((line) => line.slot.slotType === 'IMAGE_UPLOAD');

  // ─── Step-level font state (UV text) ────────────────────────────────────────
  // Font config comes from line 0's slot; the user's choice is stored on any
  // filled line and mirrored across all filled lines in the group.

  const line0Slot = inputLines[0]?.slot ?? null;

  const parsedAllowedFontsForGroup = useMemo<string[]>(() => {
    const allowedFonts = line0Slot?.allowedFonts;
    if (!allowedFonts) return [];
    if (Array.isArray(allowedFonts)) return allowedFonts;
    if (typeof allowedFonts === 'string') {
      try {
        return JSON.parse(allowedFonts) as string[];
      } catch {
        return [];
      }
    }
    return [];
  }, [line0Slot?.allowedFonts]);

  const hasFontOptionsForGroup = parsedAllowedFontsForGroup.length > 0;

  const selectedFontForGroup = useMemo<string>(() => {
    // Font may live on any filled line — line 0 is often empty when the user starts on line 2/3.
    for (const { inputValue } of inputLines) {
      const fontFromLine = getAlphanumericFont(inputValue);
      if (fontFromLine && parsedAllowedFontsForGroup.includes(fontFromLine)) {
        return fontFromLine;
      }
    }

    if (line0Slot?.defaultFont && parsedAllowedFontsForGroup.includes(line0Slot.defaultFont)) {
      return line0Slot.defaultFont;
    }
    return parsedAllowedFontsForGroup[0] ?? '';
  }, [inputLines, parsedAllowedFontsForGroup, line0Slot?.defaultFont]);

  /** Preview text for each filled input line, in line order. */
  const fontPreviewLines = useMemo<string[]>(() => {
    return inputLines
      .map(({ inputValue }) => getAlphanumericText(inputValue)?.trim())
      .filter((text) => text.length > 0);
  }, [inputLines]);

  /**
   * Applies a font to every input line in the active group that already has text.
   * Empty lines inherit the font automatically when the user starts typing
   * (font is injected by handleInputLineChange for lines beyond the first).
   */
  const handleGroupFontChange = useCallback(
    (fontName: string) => {
      if (!parsedAllowedFontsForGroup.includes(fontName)) return;

      inputLines.forEach(({ slot, inputValue }) => {
        if (slot.slotType !== 'ALPHANUMERIC') return;

        const typedValue = inputValue as AlphanumericValue | string | null;
        const currentText =
          typedValue && typeof typedValue === 'object'
            ? typedValue.text ?? ''
            : typeof typedValue === 'string'
            ? typedValue
            : '';

        if (!currentText.trim()) return;

        const currentTranslatedText =
          typedValue && typeof typedValue === 'object'
            ? (typedValue as AlphanumericValue).translatedText ?? currentText
            : currentText;

        updateSelections({
          slotId: slot.id,
          itemSelected: { text: currentText, translatedText: currentTranslatedText, font: fontName },
          groupLineItemPropertyKey: resolvedActiveGroupName
        });
      });
    },
    [parsedAllowedFontsForGroup, inputLines, resolvedActiveGroupName, updateSelections]
  );

  const handleInputLineChange = useCallback(
    (inputLineIndex: number, slotId: string, rawNewValue: unknown) => {
      const isAlphanumericObject =
        rawNewValue &&
        typeof rawNewValue === 'object' &&
        !(rawNewValue instanceof File) &&
        'text' in rawNewValue;

      const alphanumericValue = isAlphanumericObject ? (rawNewValue as AlphanumericValue) : null;

      // Lines beyond the first inherit the group's current font when first filled
      // (avoids resetting to default when the user starts typing on line 2/3).
      let processedValue: unknown = rawNewValue;
      if (inputLineIndex > 0 && isAlphanumericObject && !alphanumericValue?.font && selectedFontForGroup) {
        processedValue = { ...alphanumericValue, font: selectedFontForGroup };
      }

      const itemSelected = isAlphanumericObject && alphanumericValue?.text === '' ? null : processedValue;

      updateSelections({ slotId, itemSelected, groupLineItemPropertyKey: resolvedActiveGroupName });

      // When line 0 changes font, apply it to other lines that already have content.
      if (inputLineIndex === 0 && isAlphanumericObject && alphanumericValue?.font) {
        const inputLineValues = getInputLineValuesForGroup({
          optionGroup: activeOptionGroup,
          selections: selectionSlots,
          inputLineCount
        });
        inputLineValues[0] = alphanumericValue.text !== '' ? alphanumericValue : null;

        for (let idx = 1; idx < activeOptionGroup.slots.length; idx++) {
          const siblingSlot = getSlotForInputLine(activeOptionGroup, idx);
          const siblingValue = (inputLineValues[idx] ?? null) as AlphanumericValue | null;
          if (!siblingSlot || !siblingValue || typeof siblingValue !== 'object' || !siblingValue.text) continue;
          updateSelections({
            slotId: siblingSlot.id,
            itemSelected: { ...siblingValue, font: alphanumericValue.font },
            groupLineItemPropertyKey: resolvedActiveGroupName
          });
        }
      }
    },
    [
      activeOptionGroup,
      resolvedActiveGroupName,
      selectedFontForGroup,
      inputLineCount,
      selectionSlots,
      updateSelections
    ]
  );

  /**
   * Called by PersonalisationGroupedSlot when an image is saved in any group slot.
   * Immediately propagates the same image data to every other group at the same input line
   * so users never have to re-upload when switching group options.
   */
  const handleImageUploadedInGroup = useCallback(
    (inputLineIndex: number, imageData: ImageSavedData) => {
      const { selected, extraImages: extraImagesArray, supportingImage } = imageData;
      if (!selected) return;

      for (const group of optionGroups) {
        if (group.groupName === resolvedActiveGroupName) continue;

        const siblingSlot = getSlotForInputLine(group, inputLineIndex);
        if (!siblingSlot) continue;

        // groupLineItemPropertyKey: null marks this as a propagated staging copy,
        // not an explicit user choice. inferActiveGroupName uses this to determine
        // which group is active without false positives.
        updateSelections({
          slotId: siblingSlot.id,
          itemSelected: selected,
          replaceExtraImages: extraImagesArray ?? [],
          supportingImage: supportingImage ?? null,
          groupLineItemPropertyKey: null
        });
      }
    },
    [optionGroups, resolvedActiveGroupName, updateSelections]
  );

  const handleGroupChange = useCallback(
    (newGroupName: string) => {
      if (!newGroupName || newGroupName === resolvedActiveGroupName) return;

      const currentGroup =
        optionGroups.find((group) => group.groupName === resolvedActiveGroupName) ?? activeOptionGroup;

      const inputLineValues = getInputLineValuesForGroup({
        optionGroup: currentGroup,
        selections: selectionSlots,
        inputLineCount
      });

      // Clear only non-image inactive slots. Image slots are propagated immediately on upload
      // (via handleImageUploadedInGroup) so they should never need clearing.
      const allSlotsInGroups = optionGroups.flatMap((g) => g.slots);
      const inactiveSlotIds = getInactiveGroupSlotIds(optionGroups, newGroupName);
      inactiveSlotIds.forEach((slotId) => {
        const slotType = allSlotsInGroups.find((s) => s.id === slotId)?.slotType;
        if (slotType !== 'IMAGE_UPLOAD') {
          updateSelections({ slotId, itemSelected: null });
        }
      });

      setActiveGroupNameOverride(newGroupName);

      const newGroup = optionGroups.find((group) => group.groupName === newGroupName);
      if (!newGroup) return;

      for (let inputLineIndex = 0; inputLineIndex < inputLineCount; inputLineIndex++) {
        const targetSlot = getSlotForInputLine(newGroup, inputLineIndex);
        if (!targetSlot) continue;

        if (targetSlot.slotType === 'IMAGE_UPLOAD') {
          // For image slots: transfer the "active choice" signal by updating
          // groupLineItemPropertyKey. Old group's slot → null, new group's slot → newGroupName.
          // inferActiveGroupName reads groupLineItemPropertyKey for image groups.

          const oldSlot = getSlotForInputLine(currentGroup, inputLineIndex);
          if (oldSlot) {
            const oldSelection = selectionSlots.find((s) => s.id === oldSlot.id) as
              | { selected: unknown; extraImages: unknown; supportingImage: unknown }
              | undefined;
            if (oldSelection?.selected) {
              updateSelections({
                slotId: oldSlot.id,
                itemSelected: oldSelection.selected,
                replaceExtraImages: (oldSelection.extraImages as Array<unknown>) ?? [],
                supportingImage: (oldSelection.supportingImage as File | null) ?? null,
                groupLineItemPropertyKey: null
              });
            }
          }

          const newSelection = selectionSlots.find((s) => s.id === targetSlot.id) as
            | { selected: unknown; extraImages: unknown; supportingImage: unknown }
            | undefined;
          if (newSelection?.selected) {
            updateSelections({
              slotId: targetSlot.id,
              itemSelected: newSelection.selected,
              replaceExtraImages: (newSelection.extraImages as Array<unknown>) ?? [],
              supportingImage: (newSelection.supportingImage as File | null) ?? null,
              groupLineItemPropertyKey: newGroupName
            });
          }
          continue;
        }

        // Text slot — copy value across.
        const lineValue = inputLineValues[inputLineIndex];
        if (lineValue === null || lineValue === undefined || lineValue === '') continue;

        const isEmptyText =
          typeof lineValue === 'object' &&
          lineValue !== null &&
          'text' in lineValue &&
          (!(lineValue as AlphanumericValue).text || ((lineValue as AlphanumericValue).text ?? '').trim() === '');

        if (isEmptyText) continue;

        updateSelections({ slotId: targetSlot.id, itemSelected: lineValue, groupLineItemPropertyKey: newGroupName });
      }
    },
    [activeOptionGroup, optionGroups, resolvedActiveGroupName, inputLineCount, selectionSlots, updateSelections]
  );

  return {
    optionGroups,
    inputLineCount,
    inputLines,
    activeGroupName: resolvedActiveGroupName,
    optionSelectionChoices,
    shouldShowChooseOption,
    isImageUploadStep,
    hasUserProvidedContent,
    parsedAllowedFontsForGroup,
    hasFontOptionsForGroup,
    selectedFontForGroup,
    fontPreviewLines,
    handleGroupFontChange,
    handleInputLineChange,
    handleGroupChange,
    handleImageUploadedInGroup
  };
}
